import { NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import {
    RHProcurementOffer,
    RHProcurementPart,
    RHProcurementPartOffer,
    RHProcurementPublic
} from "../../../../models/prodModels"
import { RHCore } from "../../Core/RHCore"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { formatRHDateTimeValue } from "../../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHButtons, RHColors, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementPartDisplay } from "../../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHMakeOfferViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import type { RHOfferCapabilities, RHPartOfferInput } from "../../SocketClientFunctions"
import { RHProcurementPartView } from "../RHProcurementPartView"
import { EnterProcurementItemOfferView } from "./EnterProcurementItemOfferView"
import { OfferPartCardView } from "./OfferPartCardView"
import { RHProcurementPartOfferGrouping } from "./RHProcurementPartOfferGrouping"
import { populatedValueOfReference } from "../../Core/RHReference"
import { messageOfError } from "../../Core/RHErrorMessage"


class SubmittedPartOfferDialogView extends UIView {

    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Muuda pakkumust",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textPrimary,
            textAlignment: UITextView.textAlignment.center
        })
        .addedAsSubviewToView(this) as UITextView

    readonly errorBanner = new RHAlertBannerView()
        .configuredWithObject({
            alertType: RHAlertBannerView.type.Error
        }) as RHAlertBannerView

    readonly editorView = new EnterProcurementItemOfferView()
        .addedAsSubviewToView(this) as EnterProcurementItemOfferView

    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Loobu" },
            variant: RHButtons.exitUtilityVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: { fontSize: RHButtons.compactPrimaryFontSize }
        })
        .addedAsSubviewToView(this) as RHButton

    readonly saveButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Salvesta muudatus" },
            variant: RHMakeOfferViewTokens.confirmButtonVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: { fontSize: RHButtons.compactPrimaryFontSize }
        })
        .addedAsSubviewToView(this) as RHButton

    private _part?: RHProcurementPart
    private _partOffer?: RHProcurementPartOffer

    async configure(
        part: RHProcurementPart,
        partOffer: RHProcurementPartOffer | undefined,
        procurementTitle: string,
        procurementId: string,
        numberOfParts: number,
        offerId?: string
    ) {
        this._part = part
        this._partOffer = partOffer
        if (partOffer) {
            this.titleLabel.text = `Muuda pakkumust hankes "${RHTextSanitizer.escapedHTML(procurementTitle)}"`
            this.saveButton.titleLabel.text = "Salvesta muudatus"
        }
        else {
            this.titleLabel.text = `Lisa pakkumus hankes "${RHTextSanitizer.escapedHTML(procurementTitle)}"`
            this.saveButton.titleLabel.text = "Lisa pakkumus"
        }
        // configureForPart() alone never wires up the file-upload component's
        // procurementId/offerId (that's configureForOffer()'s job) - without
        // this, adding a new file here fails immediately with "Hange ei ole
        // määratud" even though existing files still show fine (their display
        // doesn't need procurementId, only starting a fresh upload does).
        this.editorView.configureForOffer({ procurementId, offerId })
        await this.editorView.configureForPart(part, partOffer, YES, numberOfParts)
        this.editorView.deleteButton.removeFromSuperview()
        this._hideError()
        this.setNeedsLayoutUpToRootView()
    }

    get partOfferInput(): RHPartOfferInput | undefined {
        if (!this._part || !this.editorView.hasPrice || this.editorView.priceValue === undefined) {
            return undefined
        }
        return {
            partId: this._part._id.toString(),
            price: {
                amount: this.editorView.priceValue,
                currency: "EUR"
            },
            comments: this._partOffer?.offererComments,
            fileIds: this.editorView.optionalFileIds,
            requiredDocumentFileIds: this.editorView.requiredDocumentFileIds
        }
    }

    validationError(): string | null {
        if (!this.editorView.hasPrice || this.editorView.priceValue === undefined) {
            return "Palun sisesta pakkumuse hind."
        }
        for (const slot of this.editorView._documentRequirementSlots) {
            if (slot.uploadView.uploadedFiles.length === 0) {
                return `Nõutud dokument "${slot.document.name}" on üles laadimata.`
            }
        }
        return null
    }

    showError(message: string) {
        this.errorBanner.text = message
        this.errorBanner.addedAsSubviewToView(this)
        this.setNeedsLayoutUpToRootView()
    }

    private _hideError() {
        this.errorBanner.removeFromSuperview()
    }

    private _buttonWidth(button: RHButton, availableWidth: number): number {
        return [
            [
                button.titleLabel.intrinsicContentWidth(RHMakeOfferViewTokens.actionButtonHeight) +
                RHButtons.compactPrimaryContentPadding * 2 +
                RHSpacing.large,
                RHMakeOfferViewTokens.actionButtonMinWidth
            ].max(),
            RHMakeOfferViewTokens.actionButtonMaxWidth,
            availableWidth
        ].min()
    }

    override layoutSubviews() {
        super.layoutSubviews()

        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(padding)

        let current = bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)

        if (this.errorBanner.superview === this) {
            current = current
                .rectangleForNextRow(padding, this.errorBanner)
                .assignedAsFrameOfView(this.errorBanner)
        }

        current = current
            .rectangleForNextRow(padding, this.editorView)
            .assignedAsFrameOfView(this.editorView)

        const buttonRowFrame = current.rectangleForNextRow(
            padding * 1.5,
            RHMakeOfferViewTokens.actionButtonHeight
        )
        const buttonSlotWidth = (buttonRowFrame.width - RHSpacing.medium) / 2
        const cancelWidth = this._buttonWidth(this.cancelButton, buttonSlotWidth)
        const saveWidth = this._buttonWidth(this.saveButton, buttonSlotWidth)
        const rowWidth = cancelWidth + RHSpacing.medium + saveWidth

        buttonRowFrame
            .rectangleWithWidth(rowWidth, 0.5)
            .framesByDistributingViewsAsRow(
                [this.cancelButton, this.saveButton],
                RHSpacing.medium,
                [cancelWidth, saveWidth]
            )
    }

    override intrinsicContentWidth(): number {
        return RHMakeOfferViewTokens.submittedOfferEditDialogWidth
    }

    override intrinsicContentHeight(constrainingWidth = 0): number {
        const padding = this.core.paddingLength
        const width = constrainingWidth || this.bounds.width || this.intrinsicContentWidth()
        const contentWidth = Math.max(0, width - padding * 2)

        let height = padding
        height += this.titleLabel.intrinsicContentHeight(contentWidth)
        if (this.errorBanner.superview === this) {
            height += padding + this.errorBanner.intrinsicContentHeight(contentWidth)
        }
        height += padding + this.editorView.intrinsicContentHeight(contentWidth)
        height += padding * 1.5 + RHMakeOfferViewTokens.actionButtonHeight
        return height + padding
    }

}


// ─────────────────────────────────────────────────────────────────────────────
// Per-part action row: "Muuda pakkumust" / "Tühista pakkumus"
// ─────────────────────────────────────────────────────────────────────────────

class OfferPartActionRow extends UIView {

    static buttonHeight = 32

    readonly editButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Muuda pakkumust" },
            variant: RHButtons.exitUtilityVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: { fontSize: "13px" },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => { this._editWasRequested() }
            }
        })
        .addedAsSubviewToView(this) as RHButton

    readonly rescindButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Tühista pakkumus" },
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: { fontSize: "13px" },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => { this._rescindWasRequested() }
            }
        })
        .addedAsSubviewToView(this) as RHButton

    private _editWasRequested: () => void
    private _rescindWasRequested: () => void

    constructor(editWasRequested: () => void, rescindWasRequested: () => void) {
        super()
        this._editWasRequested = editWasRequested
        this._rescindWasRequested = rescindWasRequested
    }

    private _buttonWidth(button: RHButton): number {
        return button.titleLabel.intrinsicContentWidth(OfferPartActionRow.buttonHeight) +
            RHButtons.compactPrimaryContentPadding * 2 +
            RHSpacing.large
    }

    override layoutSubviews() {
        super.layoutSubviews()

        const gap = this.core.paddingLength * 0.5
        const bounds = this.contentBoundsWithInset(0)

        const editWidth = this._buttonWidth(this.editButton)
        const rescindWidth = this._buttonWidth(this.rescindButton)
        const totalWidth = editWidth + gap + rescindWidth
        const startX = bounds.x + Math.max(0, (bounds.width - totalWidth) / 2)

        const editFrame = bounds
            .rectangleWithX(startX)
            .rectangleWithWidth(editWidth)
            .assignedAsFrameOfView(this.editButton)

        bounds
            .rectangleWithX(editFrame.max.x + gap)
            .rectangleWithWidth(rescindWidth)
            .assignedAsFrameOfView(this.rescindButton)
    }

    override intrinsicContentHeight(): number {
        return OfferPartActionRow.buttonHeight
    }

}


class SubmittedPartOfferAddRow extends UIView {

    readonly addOfferButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "+ Lisa pakkumus" },
            variant: RHButtons.compactPrimaryVariant,
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: { fontSize: RHButtons.compactPrimaryFontSize },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => { this._addOfferWasRequested() }
            }
        })
        .addedAsSubviewToView(this) as RHButton

    _addOfferWasRequested: () => void

    constructor(addOfferWasRequested: () => void) {
        super()
        this._addOfferWasRequested = addOfferWasRequested
    }

    override layoutSubviews() {
        super.layoutSubviews()

        const bounds = this.contentBoundsWithInset(0)
        const buttonWidth = [
            [
                this.addOfferButton.titleLabel.intrinsicContentWidth(RHButtons.compactPrimaryHeight) +
                RHButtons.compactPrimaryContentPadding * 2 +
                RHSpacing.large,
                240
            ].max(),
            320,
            bounds.width
        ].min()
        this.addOfferButton.frame = bounds.rectangleWithWidth(buttonWidth, 0.5)
    }

    override intrinsicContentHeight(): number {
        return RHButtons.compactPrimaryHeight
    }

}


// ─────────────────────────────────────────────────────────────────────────────
// Offerer view of an already-submitted offer (pre-deadline).
//
// Mirrors the procurer's card layout while keeping every submitted alternative
// in its own OfferPartCardView with the relevant edit/rescind actions.
// ─────────────────────────────────────────────────────────────────────────────

export class OfferSubmittedView extends UIView {

    didRescindOffer?: () => void
    didUpdateOffer?: (updatedOffer: RHProcurementOffer) => void

    _contentViews: UIView[] = []
    private _procurement?: RHProcurementPublic
    private _submittedOffer?: RHProcurementOffer
    _closedOffers: RHProcurementOffer[] = []
    _offerCapabilities?: RHOfferCapabilities
    private _deadlinePassed = false
    private _isBusy = false

    // ── Public API ────────────────────────────────────────────────────────────

    configure(
        procurement: RHProcurementPublic,
        submittedOffer: RHProcurementOffer,
        closedOffers: RHProcurementOffer[] = []
    ) {
        this._procurement = procurement
        this._submittedOffer = submittedOffer
        this._closedOffers = closedOffers
        this._deadlinePassed = this._checkDeadlinePassed(procurement)
        this._buildPartCards(procurement, submittedOffer)
        this.setNeedsLayoutUpToRootView()
    }


    set offerCapabilities(capabilities: RHOfferCapabilities | undefined) {
        this._offerCapabilities = capabilities
        if (this._procurement && this._submittedOffer) {
            this._buildPartCards(this._procurement, this._submittedOffer)
            this.setNeedsLayoutUpToRootView()
        }
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private _buildPartCards(procurement: RHProcurementPublic, submittedOffer: RHProcurementOffer) {
        this._contentViews.forEach(view => view.removeFromSuperview())
        this._contentViews = []

        const offersByPartOffer = RHProcurementPartOfferGrouping.offersByPartOffer([
            submittedOffer,
            ...this._closedOffers
        ])
        const partOffersByPartId = RHProcurementPartOfferGrouping.partOffersByPartId([
            ...offersByPartOffer.keys()
        ])
        const finalizedPartIds = new Set(
            (procurement.finalizedPartIds ?? []).map(partId => partId.toString())
        )
        const closedWithoutContractPartIds = new Set(
            (procurement.closedWithoutContractPartIds ?? []).map(partId => partId.toString())
        )

        const partCount = procurement.parts.length
        for (const part of procurement.parts) {
            const partId = part._id.toString()
            const offersForPart = partOffersByPartId.get(partId) ?? []
            const isClosedWithoutContract = closedWithoutContractPartIds.has(partId)
            const isFinalized = finalizedPartIds.has(partId) || isClosedWithoutContract
            if (isClosedWithoutContract && offersForPart.length === 0) {
                const closedPartView = new RHProcurementPartView()
                    .addedAsSubviewToView(this) as RHProcurementPartView
                closedPartView.configureWithPartData(part, YES, undefined, partCount, YES)
                this._contentViews.push(closedPartView)
            }
            for (const partOffer of offersForPart) {
                const card = new OfferPartCardView()
                    .addedAsSubviewToView(this) as OfferPartCardView
                card.configureWithPartOffer(
                    part,
                    partOffer,
                    partCount
                )
                card.setFooterViews(this._footerViewsForPartCard(
                    part,
                    partOffer,
                    isFinalized,
                    isClosedWithoutContract,
                    offersByPartOffer.get(partOffer)
                ))
                this._contentViews.push(card)
            }

            if (OfferSubmittedView._canAddSubmittedPartOffer(
                procurement.maxOffersPerPart,
                offersForPart.length,
                this._offerCapabilities?.canSubmitOffer,
                this._deadlinePassed,
                isFinalized
            )) {
                if (offersForPart.length === 0) {
                    const partView = new RHProcurementPartView().addedAsSubviewToView(this)
                    partView.configureWithPartData(part, YES, undefined, partCount)
                    this._contentViews.push(partView)
                }
                const addOfferRow = new SubmittedPartOfferAddRow(
                    () => { void this._openPartOfferDialog(part) }
                ).addedAsSubviewToView(this) as SubmittedPartOfferAddRow
                this._contentViews.push(addOfferRow)
            }
        }
    }


    _footerViewsForPartCard(
        part: RHProcurementPart,
        partOffer: RHProcurementPartOffer,
        partIsFinalized: boolean,
        isClosedWithoutContract: boolean,
        submittedOffer = this._submittedOffer
    ): UIView[] {
        let submissionDetails = "Esitas: Nimi pole määratud"
        if (submittedOffer) {
            submissionDetails = OfferSubmittedView._detailsHTMLForOffer(submittedOffer)
        }
        const submittedBanner = new RHAlertBannerView({
            text: `Oled sellele osale pakkumuse esitanud.<br>${submissionDetails}`,
            alertType: RHAlertBannerView.type.Success,
            textPresentation: RHAlertBannerView.textPresentation.Outcome
        })
        if (isClosedWithoutContract) {
            submittedBanner.configureBannerWithObject({
                text: RHProcurementPartDisplay.closedWithoutContractStatusText(this._procurement?.parts.length) +
                    `<br>${submissionDetails}`,
                alertType: RHAlertBannerView.type.Warning
            })
            return [submittedBanner]
        }

        // Older rolling-deployment responses omitted capabilities and relied on the server guard,
        // so absent capabilities must not hide the row.
        if (
            partIsFinalized ||
            this._deadlinePassed ||
            this._offerCapabilities?.canSubmitOffer === NO
        ) {
            return [submittedBanner]
        }

        const actionRow = new OfferPartActionRow(
            () => { void this._openPartOfferDialog(part, partOffer) },
            () => { this._confirmRescindPartOffer(part, partOffer) }
        )
        return [submittedBanner, actionRow]
    }

    private _confirmRescindPartOffer(part: RHProcurementPart, partOffer: RHProcurementPartOffer) {
        const partTitle = RHProcurementPartDisplay.partHeaderText(
            part,
            this._procurement?.parts.length
        )
        let question = "Kas soovid tühistada selle pakkumuse?"
        if (partTitle) {
            question = `Kas soovid tühistada pakkumuse osale „${partTitle}“?`
        }
        const shower = CBDialogViewShower.showQuestionDialog(question)
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            void this._rescindPartOffer(partOffer)
        }
    }

    async _openPartOfferDialog(
        part: RHProcurementPart,
        partOffer?: RHProcurementPartOffer
    ) {
        if (this._isBusy) {
            return
        }

        const dialogView = new SubmittedPartOfferDialogView()
        try {
            await dialogView.configure(
                part,
                partOffer,
                this._procurement?.title ?? "",
                this._procurement?._id?.toString() ?? "",
                this._procurement?.parts.length ?? 1,
                this._submittedOffer?._id?.toString()
            )
        }
        catch (error) {
            let errorMessage = "Pakkumuse vormi avamine ebaõnnestus. Palun proovi uuesti."
            if (partOffer) {
                errorMessage = "Pakkumuse olemasolevaid dokumente ei õnnestunud laadida. Palun proovi uuesti."
            }
            console.error("Error configuring submitted part offer dialog:", error)
            CBDialogViewShower.alert(errorMessage)
            return
        }
        const shower = CBDialogViewShower.showDialog(dialogView, undefined, NO)

        dialogView.cancelButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            shower.dialogView.dismiss()
        }

        dialogView.saveButton.controlEventTargetAccumulator.PrimaryActionTriggered = async () => {
            const validationError = dialogView.validationError()
            if (validationError) {
                dialogView.showError(validationError)
                return
            }

            const input = dialogView.partOfferInput
            if (!input) {
                dialogView.showError("Pakkumuse andmed on puudulikud.")
                return
            }

            shower.dialogView.dismiss()
            if (partOffer) {
                await this._updatePartOffer(partOffer, input)
            }
            else {
                await this._addPartOffer(input)
            }
        }
    }

    async _addPartOffer(input: RHPartOfferInput) {
        if (!this._submittedOffer?._id || !this._procurement?._id || this._isBusy) {
            return
        }

        this._isBusy = YES
        CBDialogViewShower.showActionIndicatorDialog("Pakkumuse lisamine...")
        let errorMessage: string | undefined

        try {
            const updatedOffer = await RHCore.sharedInstance.offerStore.addSubmittedPartOffer(
                this._submittedOffer._id + "",
                input
            )

            this._submittedOffer = updatedOffer
            this._buildPartCards(this._procurement, updatedOffer)
            this.setNeedsLayoutUpToRootView()
            this.didUpdateOffer?.(updatedOffer)
        }
        catch (error) {
            console.error("Error adding submitted part offer:", error)
            errorMessage = messageOfError(error) ?? "Viga pakkumuse lisamisel"
        }
        finally {
            CBDialogViewShower.hideActionIndicatorDialog()
            this._isBusy = NO
        }

        if (errorMessage) {
            CBDialogViewShower.alert(errorMessage)
        }
    }

    private _checkDeadlinePassed(procurement: RHProcurementPublic): boolean {
        if (!procurement.deadlineDate) {
            return false
        }
        return new Date(procurement.deadlineDate) < new Date()
    }

    private async _updatePartOffer(partOffer: RHProcurementPartOffer, input: RHPartOfferInput) {
        if (!this._submittedOffer?._id || !this._procurement?._id || !partOffer._id || this._isBusy) {
            return
        }

        this._isBusy = YES
        CBDialogViewShower.showActionIndicatorDialog("Pakkumuse muutmine...")
        let errorMessage: string | undefined

        try {
            const updatedOffer = await RHCore.sharedInstance.offerStore.updateSubmittedPartOffer(
                this._submittedOffer._id + "",
                partOffer._id + "",
                input
            )

            this._submittedOffer = updatedOffer
            this._buildPartCards(this._procurement, updatedOffer)
            this.setNeedsLayoutUpToRootView()
            this.didUpdateOffer?.(updatedOffer)
        }
        catch (error) {
            console.error("Error updating part offer:", error)
            errorMessage = messageOfError(error) ?? "Viga pakkumuse muutmisel"
        }
        finally {
            CBDialogViewShower.hideActionIndicatorDialog()
            this._isBusy = NO
        }

        if (errorMessage) {
            CBDialogViewShower.alert(errorMessage)
        }
    }

    private async _rescindPartOffer(partOffer: RHProcurementPartOffer) {
        if (!this._submittedOffer?._id || !this._procurement?._id || !partOffer._id || this._isBusy) {
            return
        }

        this._isBusy = YES
        CBDialogViewShower.showActionIndicatorDialog("Pakkumuse tühistamine...")
        let errorMessage: string | undefined

        try {
            const updatedOffer = await RHCore.sharedInstance.offerStore.rescindSubmittedPartOffer(
                this._submittedOffer._id + "",
                partOffer._id + ""
            )

            if (updatedOffer?.partOffers?.length) {
                this._submittedOffer = updatedOffer
                this._buildPartCards(this._procurement, updatedOffer)
                this.setNeedsLayoutUpToRootView()
                this.didUpdateOffer?.(updatedOffer)
            }
            else {
                this.didRescindOffer?.()
            }
        }
        catch (error) {
            console.error("Error rescinding part offer:", error)
            errorMessage = messageOfError(error) ?? "Viga pakkumuse tühistamisel"
        }
        finally {
            CBDialogViewShower.hideActionIndicatorDialog()
            this._isBusy = NO
        }

        if (errorMessage) {
            CBDialogViewShower.alert(errorMessage)
        }
    }


    // ── Layout ────────────────────────────────────────────────────────────────

    override layoutSubviews() {
        super.layoutSubviews()

        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(0)
        const contentFrame = bounds

        if (this._contentViews.length > 0) {
            contentFrame.framesByDistributingViewsAsColumn(
                this._contentViews,
                padding * 1.5,
                this._contentViews
            )
        }
    }

    override intrinsicContentHeight(constrainingWidth = 0): number {
        const padding = this.core.paddingLength
        const width = constrainingWidth || this.bounds.width

        let height = 0
        this._contentViews.forEach((view, index) => {
            if (index > 0) {
                height += padding * 1.5
            }
            height += view.intrinsicContentHeight(width)
        })
        return height
    }


    static _detailsHTMLForOffer(offer: Pick<RHProcurementOffer, "submittedAt" | "submittedBy">): string {
        const displayName = populatedValueOfReference(offer.submittedBy)?.displayName
        let submitterName = "Nimi pole määratud"
        if (typeof displayName === "string" && displayName.trim()) {
            submitterName = displayName.trim()
        }
        const submittedAt = formatRHDateTimeValue(offer.submittedAt)
        let detailsText = `Esitas: ${submitterName}`
        if (submittedAt) {
            detailsText = `Esitas: ${submitterName}, ${submittedAt}`
        }
        return RHTextSanitizer.escapedHTML(detailsText)
    }


    static _canAddSubmittedPartOffer(
        maxOffersPerPart: number | null | undefined,
        activePartOfferCount: number,
        canSubmitOffer: boolean | undefined,
        deadlineHasPassed: boolean,
        partIsFinalized: boolean = NO
    ): boolean {
        if (partIsFinalized || deadlineHasPassed || canSubmitOffer === NO) {
            return NO
        }

        if (typeof maxOffersPerPart === "number") {
            if (maxOffersPerPart === 0) {
                return YES
            }
            return activePartOfferCount < maxOffersPerPart
        }

        return activePartOfferCount < 1
    }

}
